import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react"

/**
 * Where a user should be sent back to after they follow an "Edit config" link
 * from a config-driven surface into the dedicated config page.
 */
export interface ConfigReturnTarget {
  /** Human label for the originating surface, e.g. "New registration". */
  label: string
  /** Path (pathname + search) to return to. */
  path: string
  /**
   * Pathname the user was redirected TO (the config page). The banner is a
   * consequence of that specific redirect, so once the user navigates off this
   * destination the banner is dismissed.
   */
  destination: string
}

interface ConfigReturnContextValue {
  target: ConfigReturnTarget | null
  setConfigReturn: (target: ConfigReturnTarget) => void
  clear: () => void
}

const ConfigReturnContext = createContext<ConfigReturnContextValue | null>(null)

/**
 * Holds the "return to previous page" target purely in memory. Mounted once
 * near the app root (above <Routes>, alongside TourProvider) so the value
 * survives client-side route navigation from a config-driven surface into the
 * dedicated config page and back.
 *
 * Intentionally NOT persisted to storage: the return banner must only ever
 * appear as a direct consequence of the user following an "Edit config" link
 * (which calls setConfigReturn). Rehydrating a stored target on mount would
 * resurrect the banner on a fresh load/reload with no such interaction.
 */
export function ConfigReturnProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<ConfigReturnTarget | null>(null)

  const setConfigReturn = useCallback((next: ConfigReturnTarget) => {
    setTarget(next)
  }, [])

  const clear = useCallback(() => {
    setTarget(null)
  }, [])

  const value = useMemo(
    () => ({ target, setConfigReturn, clear }),
    [target, setConfigReturn, clear],
  )

  return (
    <ConfigReturnContext.Provider value={value}>
      {children}
    </ConfigReturnContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConfigReturn(): ConfigReturnContextValue {
  const ctx = useContext(ConfigReturnContext)
  if (!ctx) {
    throw new Error("useConfigReturn must be used within a ConfigReturnProvider")
  }
  return ctx
}
